import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Login = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/auth/login`, formData, { withCredentials: true });
      // Phase 53 — if the user has TOTP 2FA enabled, the backend returns
      // {requires_2fa, temp_token, ttl_seconds} and we redirect to /two-factor
      // instead of /dashboard. Temp token is kept in sessionStorage so it
      // survives the redirect but dies when the tab closes.
      if (response.data?.requires_2fa) {
        sessionStorage.setItem('twofa_temp_token', response.data.temp_token);
        toast.info('Enter the code from your authenticator app');
        navigate('/two-factor');
        return;
      }
      // Admin enforced-enrollment branch — defensive only. The backend has
      // permanently disabled 2FA enforcement (Feb 2026 user direction), so
      // this branch should never fire in practice. Kept so older backends
      // can't strand the user in a 'no session' limbo if env state drifts.
      if (response.data?.requires_2fa_enrollment) {
        sessionStorage.setItem('twofa_temp_token', response.data.temp_token);
        toast.info('You can enable 2FA later in Profile → Security (optional).');
        navigate('/two-factor-setup');
        return;
      }
      toast.success('Login successful!');
      // GA4 — login conversion event (gated by analytics consent)
      try {
        const { trackEvent, setUserId } = await import('../lib/analytics');
        if (response.data?.user?.user_id) setUserId(response.data.user.user_id);
        trackEvent('login', { method: 'password' });
      } catch { /* analytics is best-effort */ }
      // Phase 45 — Role-based redirect (Q#4=a).
      // Super admins / any admin → /admin (the hub respects per-tile perms).
      // Regular members → /dashboard. Best-effort: any failure falls back to
      // the dashboard so we never strand a user.
      try {
        const meta = await axios.get(`${API}/admin/me/permissions`, { withCredentials: true });
        if (meta.data?.is_any_admin) {
          navigate('/admin');
          return;
        }
      } catch { /* not an admin or call failed — proceed to /dashboard */ }
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen pt-20 flex items-center justify-center bg-gradient-to-b from-slate-950 to-slate-900">
      <SEO title="Login" description="Login to your IB Club India account." path="/login" noIndex />
      <div className="max-w-md w-full mx-6 my-10">
        <div className="glass-card p-6 md:p-8">
          <h1 className="text-3xl md:text-4xl font-playfair font-bold text-[#d4af37] mb-2 text-center">
            {t('auth.login.title')}
          </h1>
          <p className="text-slate-400 text-center mb-6 md:mb-8 text-sm">{t('auth.login.subtitle')}</p>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
            <div>
              <label className="block text-sm text-slate-300 mb-2">{t('auth.login.email_label')}</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-500" size={18} />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  data-testid="email-input"
                  className="w-full bg-slate-900 border border-white/10 focus:border-[#d4af37] text-white pl-10 pr-4 py-3 outline-none transition-colors"
                  placeholder={t('auth.login.email_placeholder')}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm text-slate-300 mb-2">{t('auth.login.password_label')}</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-500" size={18} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  data-testid="password-input"
                  className="w-full bg-slate-900 border border-white/10 focus:border-[#d4af37] text-white pl-10 pr-12 py-3 outline-none transition-colors"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-slate-300"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              data-testid="login-submit-button"
              className="w-full bg-[#d4af37] text-slate-950 hover:bg-[#e5c558] font-semibold py-3 tracking-wide uppercase text-sm transition-all duration-300 disabled:opacity-50"
            >
              {isSubmitting ? `${t('auth.login.submit')}…` : t('auth.login.submit')}
            </button>

            <div className="text-right -mt-2">
              <Link
                to="/forgot-password"
                data-testid="forgot-password-link"
                className="text-xs text-slate-400 hover:text-[#d4af37] transition-colors"
              >
                {t('auth.login.forgot_password')}
              </Link>
            </div>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-white/10"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-slate-900 text-slate-400">{t('auth.login.or_continue_with')}</span>
              </div>
            </div>

            <button
              onClick={handleGoogleLogin}
              data-testid="google-login-button"
              className="mt-6 w-full border border-white/10 hover:border-[#d4af37] text-white py-3 transition-colors flex items-center justify-center space-x-2"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="currentColor"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="currentColor"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="currentColor"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              <span>{t('auth.login.google_button')}</span>
            </button>
          </div>

          <p className="mt-6 text-center text-sm text-slate-400">
            {t('auth.login.no_account')}{' '}
            <Link to="/register" className="text-[#d4af37] hover:text-[#e5c558] font-semibold">
              {t('auth.login.sign_up_link')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;